import Vue from 'vue';
import S from 'string';
import i18n from '../i18n';
import iconRenderer from './iconRenderer';

export default class OverviewTableColumnRenderer {
    /**
     * @param {String} type
     * @param {*} data
     * @param {{}} [options]
     *
     * @returns {String}
     */
    static render(type, data, options = {}) {
        let functionName = S('render-' + type).camelize();

        if (typeof this[functionName] !== 'function') {
            functionName = 'renderDefault';
        }

        if (Array.isArray(data?.actions)) {
            return data.actions
                .map((item, index) => `<a href="#" data-cell-action="${index}">${this[functionName](item, options)}</a>`)
                .join(' ');
        }

        let content = this[functionName](data, options);

        // prevent double wrapping in tooltip (e.g. when using renderIconTooltip)
        if (data?.tooltip && !content.includes('hint--')) {
            const type = options.type || 'default';
            const placement = options.placement || 'left';

            content = `<abbr class="hint--rounded hint--${type} hint--${placement}" aria-label="${data.tooltip}">${content}</abbr>`;
        }

        if (!data?.action || type === 'email-permission') {
            return content;
        }

        return `<a href="#" data-cell-action>${content}</a>`;
    }

    /**
     * @param {*} data
     * @param {{[emptyLabel]: String}} [options]
     *
     * @returns {String}
     */
    static renderDefault(data, options = {}) {
        if (data === undefined || data === null || data === false) {
            return options.emptyLabel || '';
        }

        return S(data?.value || data).escapeHTML().toString().replace(/(\r\n|\n\r|\r|\n)/g, '<br>$1');
    }

    /**
     * @param {*} data
     * @param {{}} [options]
     *
     * @returns {String}
     */
    static renderActive(data, options = {}) {
        if (data === true) {
            return `<span class="label label--success">${i18n.t('overview.active')}</span>`;
        } else {
            return `<span class="label label--inactive">${i18n.t('overview.inactive')}</span>`;
        }
    }

    /**
     * @param {*} data
     * @param {{[emptyLabel]: String}} [options]
     *
     * @returns {String}
     */
    static renderCurrency(data, options = {}) {
        if (data === undefined || data === null || data === false) {
            return options.emptyLabel || '';
        }

        return i18n.c(data);
    }

    /**
     * @param {Number|null} data
     * @param {{[emptyLabel]: String}} [options]
     *
     * @returns {String}
     */
    static renderAmountToReceive(data, options = {}) {
        if (data === undefined || data === null || data === false) {
            return options.emptyLabel || '';
        }

        if (data < 0) {
            return `-${i18n.c(Math.abs(data))}`;
        }

        return i18n.c(data);
    }

    /**
     * @param {*} data
     * @param {{[emptyLabel]: String}} [options]
     *
     * @returns {String}
     */
    static renderColor(data, options = {}) {
        if (!data) {
            return options.emptyLabel || '';
        }

        return `<span class="overview__column-color" style="background-color:${data};" title="${data}"></span>`;
    }

    /**
     * @param {*} data
     * @param {{[emptyLabel]: String}} [options]
     *
     * @returns {String}
     */
    static renderDate(data, options = {}) {
        if (!data) {
            return options.emptyLabel || '';
        }

        return i18n.ds(data, 'date');
    }

    /**
     * @param {*} data
     * @param {{[emptyLabel]: String}} [options]
     *
     * @returns {String}
     */
    static renderDatetime(data, options = {}) {
        if (!data) {
            return options.emptyLabel || '';
        }

        return i18n.ds(data, 'datetime');
    }

    /**
     * @param {*} data
     * @param {{[emptyLabel]: String}} [options]
     *
     * @returns {String}
     */
    static renderIcon(data, options = {}) {
        if (!data) {
            return options.emptyLabel || '';
        }

        return iconRenderer(data);
    }

    /**
     * @param {*} data
     * @param {{[emptyLabel]: String}} [options]
     *
     * @returns {String}
     */
    static renderIconTooltip(data, options = {}) {
        if (Array.isArray(data)) {
            return data.map(item => this.renderIconTooltip(item, options)).join('');
        }

        if (!data?.icon) {
            return options.emptyLabel || '';
        }

        const icon = iconRenderer(data.icon);

        if (!data.tooltip) {
            return icon;
        }

        const type = options.type || 'default';
        const placement = options.placement || 'left';

        return `<span class="hint--rounded hint--${type} hint--${placement} hint--large" aria-label="${data.tooltip}">${icon}</span>`;
    }

    /**
     * @param {{granted: Boolean, action: Object|null}} data
     * @param {{grantedLabel: String, deniedLabel: String, settingsLabel: String}} options
     *
     * @returns {String}
     */
    static renderEmailPermission(data, options = {}) {
        if (!data) {
            return '';
        }

        const granted = data.granted === true;
        const icon = iconRenderer(granted ? 'check' : 'cross');
        const label = S(granted ? options.grantedLabel : options.deniedLabel).escapeHTML();
        let settingsButton = '';

        if (!granted && data.action) {
            const settingsLabel = S(options.settingsLabel).escapeHTML();
            settingsButton = `<a href="#" data-cell-action>${settingsLabel}</a>`;
        }

        return `<div class="invoice-email-permission">
            <span class="invoice-email-permission__icon invoice-email-permission__icon--${granted ? 'granted' : 'denied'}" aria-label="${label}" title="${label}">${icon}</span>
            ${settingsButton}
        </div>`;
    }

    /**
     * @param {*} data
     * @param {{[emptyLabel]: String}} [options]
     *
     * @returns {String}
     */
    static renderTextTooltip(data, options = {}) {
        if (Array.isArray(data)) {
            return data.map(item => this.renderTextTooltip(item, options)).join('');
        }

        const text = data?.text || '';

        if (!text) {
            return options.emptyLabel || '';
        }

        if (!data.tooltip) {
            return text;
        }

        const type = options.type || 'default';
        const placement = options.placement || 'left';

        return `<span class="overview__column-text-tooltip hint--rounded hint--${type} hint--${placement}" aria-label="${data.tooltip}">${text}</span>`;
    }

    /**
     * @param {*} data
     * @param {{[emptyLabel]: String}} [options]
     *
     * @returns {String}
     */
    static renderInfo(data, options = {}) {
        if (!data) {
            return options.emptyLabel || '';
        }

        return `<span aria-label="${data}" class="hint--left">${iconRenderer('info')}</span>`;
    }

    /**
     * @param {*} data
     * @param {{}} [options]
     *
     * @returns {String}
     */
    static renderRaw(data, options = {}) {
        if (data?.value) {
            return data.value;
        }

        return data;
    }

    /**
     * @param {*} data
     * @param {{}} [options]
     *
     * @returns {String}
     */
    static renderReadonly(data, options = {}) {
        if (data !== true) {
            return '';
        }

        return iconRenderer('lock');
    }

    /**
     * @param {*} data
     * @param {{[emptyLabel]: String}} [options]
     *
     * @returns {String}
     */
    static renderStatus(data, options = {}) {
        if (!data) {
            return options.emptyLabel || '';
        }

        return `<div class="overview-status" title="${data.title}">${iconRenderer(data.icon, 'overview-status__icon')}</div>`;
    }

    /**
     * @param {*} data
     * @param {{[emptyLabel]: String}} [options]
     *
     * @returns {String}
     */
    static renderLabel(data, options = {}) {
        if (!data) {
            return options.emptyLabel || '';
        }

        const icon = data.icon ? iconRenderer(data.icon, 'label__icon') : '';

        return `<div class="label label--${data.style}">${icon}${data.label}</div>`;
    }

    /**
     * @param {*} data
     * @param {{[emptyLabel]: String}} [options]
     *
     * @returns {String}
     */
    static renderTime(data, options = {}) {
        if (!data) {
            return options.emptyLabel || '';
        }

        return i18n.ds(data, 'time');
    }

    /**
     * @param {*} data
     * @param {{[emptyLabel]: String}} [options]
     *
     * @returns {String}
     */
    static renderVueComponent(data, options = {}) {
        if (!data) {
            return options.emptyLabel || '';
        }

        const outputWrapper = document.createElement('div');
        outputWrapper.appendChild(new Vue({ i18n, template: data }).$mount().$el);

        return outputWrapper.innerHTML;
    }
}
